<template>
  <PageLayout
    :title="$t('Projects.Project list view.Title')"
    :loading="loading"
    :error="error"
  >
    <template #main-controls>
      <BaseButton v-if="isReady" id="new-project-button" :layout="'icon-and-text'" :icon="'BasePlus'" outline @click="showAddProjectModal = true" class="btn">
        {{ $t('Projects.Project list view.Button01') }}
      </BaseButton>
      <!-- <BaseButton v-if="true" id="seed-test-collection-button" :layout="'icon-and-text'" :icon="'BasePlus'" outline @click="seedTestCollection" class="btn">
        Seed test collection
      </BaseButton> -->
    </template>
    <template #content>
      <div>
        <div class="w-full flex gap-4 mb-4">
          <BaseInput
            id="project-list-search"
            class="flex-grow"
            data-cy=""
            v-model="searchQuery"
            :label="$t('Shared.search')"
            :autoFocus="true"
            :iconName="'BaseSearch'"
          />
          <BaseSelect id="project-group-select" class="flex-grow" v-model="selectedFolder" :options="folderOptions" :label="$t('Projects.New project popup.Label04')" :placeholder="$t('Shared.choose') + ' ' + $t('Projects.New project popup.Label04').toLowerCase()" :customArrow="true" />
        </div>
        <ProjectsList v-if="searchedCompasses.length > 0" :projects="searchedCompasses" :projectsChartData="projectsChartData"/>
        <p v-else class="italic">{{ $t('Projects.Project list view.MessageNoProjects') }}</p>
        <!-- Only for Bokvalitetsrosen customers -->
        <div class="bokvalitet-info" v-if="isBokvalitetCustomer && this.compasses.length === 0">
          <p>Slik kommer du i gang:</p>
          <ol>
            <li><b>Utforsk verktøyet:</b> Bruk tid til å bli kjent med de ulike funksjonene og hvordan de kan brukes i prosjektet.</li>
            <li><b>Definer ambisjoner:</b> Bruk verktøyet til å avklare og dokumentere ambisjoner for boligkvalitet.</li>
            <li><b>Samarbeid:</b> Involver boligutviklere, arkitekter og kommunale planavdelinger i prosessen for å sikre en helhetlig tilnærming, og god kommunikasjon.</li>
            <li><b>Dokumentasjon:</b> Dokumenter vurderinger og beslutninger for å sikre sporbarhet og kvalitet.</li>
            <li><b>Skap boligmiks:</b> Bruk Bokvalitetsrosen til å utvikle en balansert boligmiks som møter beboernes behov og ønsker.</li>
          </ol>
          <p>Vi håper Bokvalitetsrosen vil være et nyttig verktøy arbeidet med å skape gode boliger.</p>
          <p>Er det noe du savner? Vi vil gjerne høre fra deg.</p>
          <p>Lykke til!</p>
        </div>
      </div>
    </template>
    <template #modals>
      <ModalAddProject v-model="showAddProjectModal" @create="createProject"/>
    </template>
  </PageLayout>
</template>

<script>
import ProjectsList from '@/components/projects/ProjectsList'
import BaseButton from '../components/shared/BaseButton.vue'
import ModalAddProject from '@/components/projects/ModalAddProject'
import { models, makeFindMixin } from '@feathersjs/vuex'
import BaseInput from '@/components/shared/BaseInput'
import BaseSelect from '@/components/shared/BaseSelect'
import createCompassQuestionsFromCollection from '@/composables/questionCollections/createCompassQuestionsFromCollection'
import PageLayout from '@/components/layouts/PageLayout.vue'
import seedTestCollection from '@/composables/goalCollections/seedTestCollection'
import { preloadProjectsData, preloadProjectsDatasets } from '@/composables/projects/preloadProjectsData'
import { countryIdToLocaleId } from '@/composables/utils'

export default {
  components: {
    ProjectsList,
    BaseButton,
    ModalAddProject,
    BaseInput,
    BaseSelect,
    PageLayout,
  },
  data () {
    return {
      error: null,
      loading: true,
      isReady: false,
      showAddProjectModal: false,
      searchQuery: '',
      selectedFolder: 'all',
      folderOptions: [{ value: 'all', label: this.$t('Shared.all') }],
      projectsChartData: new Map(), // Map of compassId -> { framework, categories, datasets }
    }
  },
  computed: {
    isBokvalitetCustomer () { // TODO This should be fixed later
      if (this.$store.getters['frameworks/list'].some(framework => framework.id === 4) && this.$store.getters['auth/user'].frameworkIds.length === 1) {
        return true
      }
      return false
    },
    compassesParams () {
      return {}
    },
    compassesFetchParams () {
      return {}
    },
    filteredCompasses () {
      if (this.selectedFolder === 'all' || this.selectedFolder === null) {
        return this.compasses
      }
      if (this.selectedFolder === 'outside') {
        return this.compasses.filter(compass => !compass.compassFolderId)
      }
      return this.compasses.filter(compass => compass.compassFolderId === this.selectedFolder)
    },
    searchedCompasses () {
      if (this.searchQuery === '') {
        return this.filteredCompasses
      }
      const foldersWithQueryInTitle = this.$store.getters['compass-folder/list'].filter(folder => folder.title.toLowerCase().includes(this.searchQuery.toLowerCase()))
      return this.filteredCompasses.filter(compass => (
        compass.title.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        compass.description.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        foldersWithQueryInTitle.some(folder => folder.id === compass.compassFolderId)
      ))
    },
  },
  mixins: [makeFindMixin({ service: 'compasses' })],
  methods: {
    async seedTestCollection () {
      await seedTestCollection()
    },
    async createProject (project) {
      if (this.loading) {
        // Prevent double-submission
        return
      }
      try {
        this.loading = true
        if (project.folderId === 'new') {
          const newProjectFolder = await new models.api.CompassFolder()
          newProjectFolder.title = project.folderTitle
          const _createdFolder = await newProjectFolder.create()
          project.folderId = _createdFolder.id
          console.log('folder created', newProjectFolder)
        }
        const newCompass = await new models.api.Compass()
        newCompass.title = project.title
        newCompass.description = project.description
        newCompass.projectNumber = project.projectNumber
        newCompass.compassFolderId = project.folderId
        newCompass.countryId = project.countryId
        newCompass.frameworkIds = project.frameworkIds
        const result = await this.$store.dispatch(
          'compasses/create',
          [newCompass, {
            query: {
              collectionId: project.collectionId,
              questionCollectionId: project.questionCollectionId,
              goalCollectionId: project.goalCollectionId,
              localeId: countryIdToLocaleId(project.countryId),
            },
          }],
        )
        console.log('new project was created', result)
        const compassId = result.id
        // Note: collectionId actions are now created in the backend after hook
        // No need to call createCompassActionsFromCollection here anymore
        if (project.questionCollectionId) {
          console.log('adding question collection')
          await createCompassQuestionsFromCollection(project.questionCollectionId, compassId)
        }
        if (project.goalCollectionId) {
          console.log('adding goal collection')
          // add to local storage, with ref to compassId
          localStorage.setItem(`goalCollection_${compassId}`, project.goalCollectionId)
        }
        console.log('redirecting to new project')
        this.$router.push(`/projekt/${compassId}`)
      } catch (e) {
        console.log(e)
        this.loading = false
      }
      // this.$store.state
    },
  },
  async mounted () {
    try {
      await this.$store.dispatch('refreshStandardActions', { isEnabled: true })
      this.isReady = true
      await this.$store.dispatch('compass-folder/find')
      const folders = this.$store.getters['compass-folder/list'].map(folder => ({ value: folder.id, label: folder.title }))
      const allFoldersOption = { value: 'all', label: this.$t('Shared.all') + ' ' + this.$t('Shared.folders').toLowerCase() }
      const outsideFolderOption = { value: 'outside', label: this.$t('Shared.outside') + ' ' + this.$t('Shared.folder').toLowerCase() }
      this.folderOptions = [allFoldersOption, ...folders, outsideFolderOption]
      this.selectedFolder = null
      await this.$store.dispatch('compasses/find')

      // Bulk preload compass data (categories, subcategories, actions) for all projects
      // This replaces individual fetches per project card
      const compassIds = this.compasses.map(p => p.id)
      await Promise.all([
        this.$store.dispatch('compass-categories/find', { query: { compassId: { $in: compassIds }, $limit: 10000 } }),
        this.$store.dispatch('compass-sub-categories/find', { query: { compassId: { $in: compassIds }, $limit: 10000 } }),
        this.$store.dispatch('compass-actions/find', { query: { compassId: { $in: compassIds }, $limit: 10000 } }),
      ])

      // Preload basic chart data (frameworks, categories) for all projects
      this.projectsChartData = await preloadProjectsData(this.compasses, this.$store)

      // Show initial UI with chart containers
      this.loading = false

      // Then load datasets in the background (these are the expensive calls)
      // This allows the page to render faster while datasets load progressively
      this.projectsChartData = await preloadProjectsDatasets(this.compasses, this.projectsChartData, this.$store)
    } catch (error) {
      console.log(error)
      this.loading = false
      this.error = 'Der opstod en fejl'
      /*
      if (error.code === 401) {
        await this.$store.dispatch('auth/logout')
        this.$router.push('/login')
      } else {
        this.error = 'Der opstod en fejl'
      }
      */
    }
  },
}
</script>

<style scoped>
.bokvalitet-info {
  margin-top: 2rem;
  padding: 1rem;
  background-color: #f5f5f5;
  border-radius: 0.5rem;
}
.bokvalitet-info p {
  margin-bottom: 0.5rem;
}
.bokvalitet-info ol {
  margin-left: 1rem;
  margin-bottom: 1rem;
}
.bokvalitet-info ol li {
  list-style: decimal;
  margin-left: 1rem;
}
</style>
